import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';
import { UserWidget, DEFAULT_WIDGETS, DEFAULT_OPERATOR_WIDGETS } from '@/config/dashboardWidgets';
import { Json } from '@/integrations/supabase/types';

export const useDashboardConfig = () => {
  const { userId, companyId } = useStableAuth();
  const { isMaster, canAccess } = usePermissions();
  const queryClient = useQueryClient();
  
  // Masters e usuários com acesso à visão financeira recebem widgets financeiros por padrão
  const defaultWidgets = (isMaster || canAccess('dashboard:view_financials'))
    ? DEFAULT_WIDGETS
    : DEFAULT_OPERATOR_WIDGETS;

  const { data: config, isLoading } = useQuery({
    queryKey: ['dashboard-config', userId, companyId],
    queryFn: async () => {
      if (!userId || !companyId) return null;

      const { data, error } = await supabase
        .from('user_dashboard_config')
        .select('*')
        .eq('user_id', userId)
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  const widgets: UserWidget[] = config?.widgets 
    ? (config.widgets as unknown as UserWidget[])
    : defaultWidgets;

  const saveConfigMutation = useMutation({
    mutationFn: async (newWidgets: UserWidget[]) => {
      if (!userId || !companyId) throw new Error('User not authenticated');

      const { data: existing } = await supabase
        .from('user_dashboard_config')
        .select('id')
        .eq('user_id', userId)
        .eq('company_id', companyId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('user_dashboard_config')
          .update({ widgets: newWidgets as unknown as Json })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_dashboard_config')
          .insert({
            user_id: userId,
            company_id: companyId,
            widgets: newWidgets as unknown as Json,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-config'] });
    },
    onError: (error) => {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao salvar configuração do dashboard');
    },
  });

  const saveConfig = (newWidgets: UserWidget[]) => {
    saveConfigMutation.mutate(newWidgets);
  };

  const addWidget = (type: string, size: 'full' | 'half' | 'third' = 'full') => {
    const newWidget: UserWidget = {
      id: `widget-${Date.now()}`,
      type,
      size,
      position: widgets.length,
    };
    saveConfig([...widgets, newWidget]);
  };

  const removeWidget = (widgetId: string) => {
    const newWidgets = widgets
      .filter(w => w.id !== widgetId)
      .map((w, index) => ({ ...w, position: index }));
    saveConfig(newWidgets);
  };

  const reorderWidgets = (newWidgets: UserWidget[]) => {
    const reordered = newWidgets.map((w, index) => ({ ...w, position: index }));
    saveConfig(reordered);
  };

  const updateWidgetSize = (widgetId: string, size: 'full' | 'half' | 'third') => {
    const newWidgets = widgets.map(w => 
      w.id === widgetId ? { ...w, size } : w
    );
    saveConfig(newWidgets);
  };

  const resetToDefault = () => {
    saveConfig(defaultWidgets);
    toast.success('Dashboard restaurado para configuração padrão');
  };

  return {
    widgets,
    isLoading,
    isSaving: saveConfigMutation.isPending,
    addWidget,
    removeWidget,
    reorderWidgets,
    updateWidgetSize,
    resetToDefault,
  };
};
